import { Fragment, useMemo, useState } from 'react';

import { AsyncBoundary, Drawer, Icon } from '@kollez/design-system';
import { useCalendar } from '@/lib/queries';

/**
 * /tpo/calendar (F90c) — direct port of kollez/pages/tpo/calendar.html.
 *   Week grid: 60px + 6 day columns (Mon–Sat) · hour rows (9 am – 5 pm)
 *   .ev blocks colored by kind (drive/int/test/hold), agenda list on mobile
 *   week paging, legend, Rooms drawer with per-room booking table.
 */

type Kind = 'drive' | 'int' | 'test' | 'hold';

interface Event {
  id: string;
  dayIdx: number;   // 0..5
  hourIdx: number;  // 0..7
  span: number;
  kind: Kind;
  title: string;
  where: string;
  meta: string;
}

const HOURS = ['9 am', '10 am', '11 am', '12 pm', '1 pm', '2 pm', '3 pm', '4 pm'];
const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function startOfWeek(d: Date): Date {
  const w = new Date(d);
  const day = w.getDay(); // 0=Sun, 1=Mon
  const diff = day === 0 ? -6 : 1 - day;
  w.setDate(w.getDate() + diff);
  w.setHours(0, 0, 0, 0);
  return w;
}

function serverKind(kind: string): Kind {
  const k = kind.toLowerCase();
  if (/interview/.test(k)) return 'int';
  if (/test|assess/.test(k)) return 'test';
  if (/holiday|hold/.test(k)) return 'hold';
  return 'drive';
}

export function TpoCalendarPage() {
  const [weekOffset, setWeekOffset] = useState(0);
  const [openId, setOpenId] = useState<string | null>(null);
  const [roomsOpen, setRoomsOpen] = useState(false);

  const weekStart = useMemo(() => {
    const s = startOfWeek(new Date());
    s.setDate(s.getDate() + weekOffset * 7);
    return s;
  }, [weekOffset]);
  const weekEnd = useMemo(() => {
    const e = new Date(weekStart);
    e.setDate(e.getDate() + 6);
    return e;
  }, [weekStart]);

  const cal = useCalendar({
    scope: 'tpo',
    from: weekStart.toISOString(),
    to: weekEnd.toISOString(),
  });

  const days = useMemo(() => DAY_NAMES.map((label, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return { label, date: d.getDate(), full: d };
  }), [weekStart]);

  const events = useMemo<Event[]>(() => {
    const list = (cal.data?.events ?? []) as Array<{ id: string; kind: string; title: string; startAt: string; endAt?: string; room?: string; subtitle?: string }>;
    return list.flatMap((e) => {
      const start = new Date(e.startAt);
      const end = e.endAt ? new Date(e.endAt) : new Date(start.getTime() + 3600000);
      const dayIdx = days.findIndex((d) => d.full.toDateString() === start.toDateString());
      if (dayIdx < 0) return [];
      const hour = start.getHours();
      const hourIdx = Math.max(0, Math.min(HOURS.length - 1, hour - 9));
      const span = Math.max(1, Math.min(HOURS.length - hourIdx, Math.ceil((end.getTime() - start.getTime()) / 3600000)));
      return [{
        id: e.id,
        dayIdx,
        hourIdx,
        span,
        kind: serverKind(e.kind),
        title: e.title,
        where: e.room ?? '—',
        meta: e.subtitle ?? '',
      }];
    });
  }, [cal.data, days]);

  const opened = openId ? events.find((e) => e.id === openId) : null;
  const today = new Date();

  return (
    <div className="stack">
      <style>{`
        .wk { display: grid; grid-template-columns: 60px repeat(6, minmax(130px, 1fr)); min-width: 860px; font-size: .8125rem; }
        .wk > div { border-right: 1px solid rgb(var(--line-tertiary)); border-bottom: 1px solid rgb(var(--line-tertiary)); min-height: 52px; padding: 3px; position: relative; }
        .wk .h { background: rgb(var(--bg-secondary)); padding: 8px 10px; min-height: 0; position: sticky; top: 0; z-index: 1; }
        .wk .h b { font-family: "Newsreader", Georgia, serif; font-weight: 500; font-size: 1.25rem; margin-left: 4px; }
        .wk .h.today { box-shadow: inset 0 -2px 0 rgb(var(--ink-accent)); }
        .wk .t { color: rgb(var(--ink-tertiary)); text-align: right; font-size: .75rem; padding: 4px 8px; }
        .ev { position: absolute; left: 3px; right: 3px; top: 3px; border-radius: 6px; padding: 4px 7px; line-height: 1.3; overflow: hidden; cursor: pointer; border-left: 3px solid; z-index: 1; }
        .ev[data-k="drive"] { background: rgb(var(--bg-info)); border-color: rgb(var(--ink-accent)); }
        .ev[data-k="int"] { background: rgb(var(--bg-success)); border-color: rgb(var(--ink-success)); }
        .ev[data-k="test"] { background: rgb(var(--bg-warning)); border-color: rgb(var(--ink-warning)); }
        .ev[data-k="hold"] { background: repeating-linear-gradient(135deg, rgb(var(--bg-tertiary)) 0 6px, transparent 6px 12px); border-color: rgb(var(--line-primary)); color: rgb(var(--ink-secondary)); }
        .agenda li { display: grid; grid-template-columns: 64px minmax(0,1fr); gap: 12px; padding: 12px 16px; border-bottom: 1px solid rgb(var(--line-tertiary)); }
        .agenda h3 { margin: 0; padding: 10px 16px 4px; font-size: .8125rem; color: rgb(var(--ink-tertiary)); font-weight: 600; }
        @media (max-width: 899px) { #wk-card { display: none; } }
        @media (min-width: 900px) { #ag-card { display: none; } }
      `}</style>

      <div className="page-header">
        <div className="ph-text">
          <h1 className="h-page">Calendar</h1>
          <p className="subhead">All drives, tests and interviews, with rooms. Holidays and exams are blocked out.</p>
        </div>
        <div className="ph-actions">
          <div className="flex items-center gap-1">
            <button className="btn btn-ghost btn-icon" aria-label="Previous week" onClick={() => setWeekOffset((n) => n - 1)}>
              <Icon name="chevron-left" size={14} />
            </button>
            <span className="text-sm font-medium px-2">
              {formatRange(weekStart, weekEnd)}
            </span>
            <button className="btn btn-ghost btn-icon" aria-label="Next week" onClick={() => setWeekOffset((n) => n + 1)}>
              <Icon name="chevron-right" size={14} />
            </button>
          </div>
          <button className="btn btn-outline" onClick={() => setRoomsOpen(true)}><Icon name="door-open" size={14} />Rooms</button>
        </div>
      </div>

      <div className="flex flex-wrap gap-4 text-sm text-ink-secondary">
        <span><span className="dot dot-accent" /> Drive day</span>
        <span><span className="dot dot-success" /> Interviews</span>
        <span><span className="dot dot-warning" /> Online test</span>
        <span><span className="dot" /> Blocked</span>
      </div>

      <AsyncBoundary
        kind="detail"
        loading={cal.isLoading}
        error={cal.error}
        empty={!cal.isLoading && events.length === 0}
        emptyTitle="No events this week"
        emptyHint="Post a drive or schedule interviews to see them here."
        onRetry={() => void cal.refetch()}
      >
        <div className="card overflow-x-auto" id="wk-card">
          <div className="wk">
            <div className="h" />
            {days.map((d) => (
              <div
                key={d.label}
                className={`h${d.full.toDateString() === today.toDateString() ? ' today' : ''}`}
              >
                {d.label}<b>{d.date}</b>
              </div>
            ))}
            {HOURS.map((h, hi) => (
              <Fragment key={`hr-${hi}`}>
                <div className="t">{h}</div>
                {days.map((_, di) => {
                  const ev = events.find((e) => e.dayIdx === di && e.hourIdx === hi);
                  return (
                    <div key={`${hi}-${di}`}>
                      {ev && (
                        <button
                          className="ev"
                          data-k={ev.kind}
                          onClick={() => setOpenId(ev.id)}
                          style={{ height: `calc(${ev.span * 100}% + ${(ev.span - 1)}px - 6px)`, textAlign: 'left', font: 'inherit', color: 'inherit', border: '0', borderLeft: '3px solid', cursor: 'pointer' }}
                        >
                          <b>{ev.title}</b><br />
                          {ev.where}
                        </button>
                      )}
                    </div>
                  );
                })}
              </Fragment>
            ))}
          </div>
        </div>

        <div className="card" id="ag-card">
          {days.map((d, di) => {
            const L = events.filter((e) => e.dayIdx === di).sort((a, b) => a.hourIdx - b.hourIdx);
            if (!L.length) return null;
            return (
              <div key={d.label}>
                <h3>{d.label} {d.date}</h3>
                <ul className="agenda m-0 p-0 list-none">
                  {L.map((e) => (
                    <li key={e.id}>
                      <span className="cell-sub">{e.kind === 'hold' && e.span === HOURS.length ? 'All day' : HOURS[e.hourIdx]}</span>
                      <div>
                        <div className="cell-title">
                          <span className={`dot ${e.kind === 'drive' ? 'dot-accent' : e.kind === 'int' ? 'dot-success' : e.kind === 'test' ? 'dot-warning' : ''}`} />
                          {' '}{e.title}
                        </div>
                        <div className="cell-sub">{e.where} · {e.meta}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </AsyncBoundary>

      {/* Deep-audit fix (Page 34/N): the old event drawer let the TPO
          'Change room' from a select whose options ('Seminar Hall A',
          'Room 305 (free)') were hardcoded and never came from a Room
          API. The Save button fired a lying toast — 'Saved. Nobody was
          notified because nothing changed.' — with no API call. The
          promise 'Students and the company are told automatically' was
          the load-bearing lie. Room-change now points the TPO into the
          drive detail where the real reschedule flow lives. */}
      <Drawer open={Boolean(opened)} onClose={() => setOpenId(null)} title={opened?.title ?? ''} size="md">
        {opened && (
          <>
            <p className="eyebrow">{days[opened.dayIdx]?.label} {days[opened.dayIdx]?.date} · {HOURS[opened.hourIdx]}</p>
            <h2 className="h-section">{opened.title}</h2>
            <dl className="dl mt-4">
              <dt>Where</dt><dd>{opened.where}</dd>
              <dt>Who</dt><dd>{opened.meta || '—'}</dd>
              <dt>Length</dt><dd>{opened.span} hour{opened.span > 1 ? 's' : ''}</dd>
            </dl>
            {opened.kind !== 'hold' && (
              <div className="drawer-foot">
                <a className="btn btn-primary" href={`/tpo/drives/${opened.id}`}>Open drive to reschedule</a>
              </div>
            )}
          </>
        )}
      </Drawer>

      {/* Deep-audit fix (Page 34/N): the Rooms drawer showed a hardcoded
          6-row list — 'Main Auditorium (600)', 'Room 201/204 (30)',
          'Seminar Hall A (180)', 'Room 305 (40)', 'Labs 1–4 (240)' —
          none of which came from a real Room API. Every college saw
          the same rooms with the same seat counts. Replaced with an
          honest 'coming soon' notice pointing at where room booking
          will live once the Room CRUD ships. */}
      <Drawer open={roomsOpen} onClose={() => setRoomsOpen(false)} title="Rooms" size="lg">
        <h2 className="h-section">Rooms this week</h2>
        <div className="notice notice-info mt-4">
          Room booking with real capacity + conflict detection ships in the next release.
          For now, coordinate room assignments with your placement office in the drive detail page.
        </div>
      </Drawer>
    </div>
  );
}

function formatRange(start: Date, end: Date): string {
  const opt: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
  const s = start.toLocaleDateString('en-IN', opt);
  const e = end.toLocaleDateString('en-IN', opt);
  const y = end.getFullYear();
  return `${s} – ${e} ${y}`;
}
